import {
  Dialog,
  DialogTitle,
  Typography,
  IconButton,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import Loading from "../../../../components/loading/loading";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import { useDispatch, useSelector } from "react-redux";
import {
  getAssignableInspectionFirmsRequest,
  getOrderDetailsRequest,
  getOrderInspectionRequest,
} from "../../../../redux/orders/slice";
import { DataGrid, GridColDef, GridRowSelectionModel } from "@mui/x-data-grid";
import dayjs from "dayjs";
import {
  assignInspectorRequest,
  orderMoveToRecruitingRequest,
} from "../../../../utils/APICalls";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import eventBus from "../../../../utils/EventBus";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import TableHeaderTextField from "../../../../components/table-header-text-filed/table-header-text-filed";
import { ClearAllOutlined } from "@mui/icons-material";

const QualifiedRenderer = ({ rowData }: any) => {
  const { orderRestriction } = rowData;
  const [isQualified, setIsQualified] = useState(false);
  const [missingQualification, setMissingQualification] = useState("");

  useEffect(() => {
    checkQualified();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowData]);

  const checkQualified = () => {
    let isBackgroundCheckValid = false;
    if (orderRestriction?.backgroundCheckDone) {
      if (
        rowData?.backgroundCheckDone === orderRestriction?.backgroundCheckDone
      ) {
        isBackgroundCheckValid = true;
      } else {
        isBackgroundCheckValid = false;
      }
    } else {
      isBackgroundCheckValid = true;
    }

    const isInsuranceValid = orderRestriction?.insurances.every(
      (item: string) => rowData?.insurances?.includes(item)
    );

    const isCertificateValid = orderRestriction?.certifications.every(
      (item: string) => rowData?.certifications?.includes(item)
    );

    if (!isInsuranceValid) {
      setMissingQualification("Insurances");
    }

    if (!isBackgroundCheckValid) {
      setMissingQualification("Background Check");
    }

    if (!isCertificateValid) {
      setMissingQualification("Certification");
    }

    if (isBackgroundCheckValid && isCertificateValid && isInsuranceValid) {
      setIsQualified(true);
    } else {
      setIsQualified(false);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", height: "inherit" }}>
      {isQualified ? (
        <Typography noWrap sx={{ ml: 1, maxWidth: 160 }}>
          Yes
        </Typography>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <ErrorOutlineIcon color="error" />
          <Typography noWrap sx={{ ml: 1, maxWidth: 160 }}>
            No - {missingQualification}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default function AssignOrderDialog({
  orderId,
  orderNumber,
  showDialog,
  closeDialog,
}: any) {
  const dispatch = useDispatch();
  const [outsideCoverage, setOutsideCoverage] = useState(false);
  const [rowData, setRowData] = useState<any>([]);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [inspectionNote, setInspectionNote] = useState("");
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [filterParams, setFilterParams] = useState({});
  const [filterResetKey, setFilterResetKey] = useState(0);

  const { orderDetails, assignableInspectionFirms } = useSelector(
    (state: any) => state.orders
  );

  const columns: GridColDef[] = [
    {
      field: "displayName",
      headerName: "Inspection Firm",
      width: 190,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="displayName"
          sortField="displayName"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "aliasName",
      headerName: "Alias Name",
      width: 150,
      sortable: false,
      renderHeader: (params) => (
        <TableHeaderTextField
          field="aliasName"
          sortField="aliasName"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "city",
      headerName: "City",
      width: 120,
      sortable: false,
      valueGetter: (value, row) => {
        return row.organizationAddress?.city || "-";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="city"
          sortField="city"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "state",
      headerName: "State",
      width: 120,
      sortable: false,
      valueGetter: (value, row) => {
        return row.organizationAddress?.state || "-";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="state"
          sortField="state"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "zip",
      headerName: "Zip",
      width: 120,
      sortable: false,
      valueGetter: (value, row) => {
        return row.organizationAddress?.zip || "-";
      },
      renderHeader: (params) => (
        <TableHeaderTextField
          field="zip"
          sortField="zip"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "hasAttempted",
      headerName: "Attempted?",
      width: 140,
      sortable: false,
      valueGetter: (value, row) => {
        return value ? `Declined - ${row.note}` : "No";
      },
      renderHeader: (params) => (
        <Typography sx={{ fontWeight: "bold" }}>Attempted?</Typography>
      ),
    },
    {
      field: "isWithinCoverageArea",
      headerName: "Distance",
      width: 200,
      sortable: false,
      valueGetter: (value) => {
        return value ? "Within Coverage Area" : "Outside Coverage Area";
      },
      renderHeader: (params) => (
        <Typography sx={{ fontWeight: "bold" }}>Distance</Typography>
      ),
    },
    {
      field: "grade",
      headerName: "Grade",
      width: 120,
      sortable: false,
      valueGetter: (value, row) => {
        return row?.gradeLetter ? `${row.gradeLetter} (${row.grade})` : "-";
      },
      renderHeader: (params) => (
        <Typography sx={{ fontWeight: "bold" }}>Grade</Typography>
      ),
    },
    {
      field: "inspectionsCompleted",
      headerName: "Inspections Completed",
      width: 140,
      sortable: false,
      renderHeader: (params) => (
        <Typography sx={{ fontWeight: "bold", textWrap: "wrap" }}>
          Inspections Completed
        </Typography>
      ),
    },
    {
      field: "qualified",
      headerName: "Qualified",
      width: 200,
      sortable: false,
      renderCell(params) {
        return <QualifiedRenderer rowData={params.row} />;
      },
      renderHeader: (params) => (
        <Typography sx={{ fontWeight: "bold" }}>Qualified</Typography>
      ),
    },
    {
      field: "currentAssignments",
      headerName: "Assigned/Max",
      width: 160,
      sortable: false,
      valueGetter: (value, row) => {
        return row?.currentAssignments !== null && row?.maxCapacity !== null
          ? `${row.currentAssignments}/${row.maxCapacity}`
          : "-";
      },
      renderHeader: (params) => (
        <Typography sx={{ fontWeight: "bold" }}>Assigned/Max</Typography>
      ),
    },
  ];

  useEffect(() => {
    dispatch(
      getAssignableInspectionFirmsRequest({
        orderId,
        showUnqualifiedAndOutsideCoverageArea: outsideCoverage,
      })
    );
  }, [orderId, outsideCoverage]); // eslint-disable-line

  useEffect(() => {
    if (assignableInspectionFirms) {
      setRowData(assignableInspectionFirms);
    }
  }, [assignableInspectionFirms]);

  const getEta = () => {
    const clientDueDateDiff = Math.ceil(
      dayjs(orderDetails?.clientDueDate).diff(Date.now(), "day", true)
    );
    return clientDueDateDiff;
  };

  const onAssignClick = async () => {
    const selectedUser = rowData.find(
      (row: any) => row?.id === rowSelectionModel[0]
    );
    if (selectedUser) {
      const isCertificateValid =
        selectedUser?.orderRestriction?.certifications.every((item: string) =>
          selectedUser?.certifications?.includes(item)
        );
      if (isCertificateValid) {
        const payload = {
          inspectorFirmId: rowSelectionModel[0],
          noteToInspector: inspectionNote,
        };
        setShowLoading(true);
        const response = await assignInspectorRequest(orderId, payload);
        setShowLoading(false);
        if (response?.status === 204) {
          dispatch(getOrderInspectionRequest({ orderId }));
          dispatch(getOrderDetailsRequest({ id: orderId }));
          closeDialog();
        } else if (response?.status === 400 || response?.status === 403) {
          setShowErrorNotification(true);
          setErrorMessage(response?.data?.detail);
        }
      } else {
        setShowErrorNotification(true);
        setErrorMessage("Certification missing, cannot assign to this user.");
      }
    }
  };

  const onRecruitingClick = async () => {
    const payload = {
      isRecruiting: true,
    };
    setShowLoading(true);
    const response = await orderMoveToRecruitingRequest(orderId, payload);
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getOrderDetailsRequest({ id: orderId }));
      eventBus.emit("moveToRecruiting");
      closeDialog();
    } else if (response?.status === 400 || response?.status === 403) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  useEffect(() => {
    //Removing filterParams object key if their value is empty
    const filters = filterParams as any;
    Object.keys(filterParams)?.forEach((key: string) => {
      if (filters.hasOwnProperty(key) && filters[key] === "") {
        delete filters[key];
      }
    });
    if (Object.keys(filters).length > 0) {
      const filteredData = processRows(assignableInspectionFirms, filters);
      setRowData(filteredData);
    } else {
      setRowData(assignableInspectionFirms);
    }
  }, [filterParams]); // eslint-disable-line

  const onFilterUsers = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          //if isActive status is false and if userType is 0 then condition also should pass
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  const normalize = (val: string) =>
    (val ?? "").toString().toLowerCase().trim();

  const processRows = (rows: any[], filters: any) => {
    const { displayName, aliasName, city, state, zip, sortColumn, sortOrder } =
      filters;

    let filtered = rows.filter((row) => {
      const dName = normalize(row.displayName);
      const aName = normalize(row.aliasName);
      const address = row.organizationAddress || {};
      const cty = normalize(address.city);
      const st = normalize(address.state);
      const zp = normalize(address.zip);

      return (
        (!displayName || dName.includes(normalize(displayName))) &&
        (!aliasName || aName.includes(normalize(aliasName))) &&
        (!city || cty.includes(normalize(city))) &&
        (!state || st.includes(normalize(state))) &&
        (!zip || zp.includes(normalize(zip)))
      );
    });

    if (sortColumn) {
      filtered.sort((a, b) => {
        let aVal, bVal;

        const getValue = (row: any, field: string) => {
          const address = row.organizationAddress || {};

          switch (field) {
            case "city":
              return address.city;
            case "state":
              return address.state;
            case "zip":
              return address.zip;
            default:
              return row[field];
          }
        };

        aVal = normalize(getValue(a, sortColumn));
        bVal = normalize(getValue(b, sortColumn));

        if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
        if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  };

  const onClearAllFilters = () => {
    setRowData(assignableInspectionFirms);
    setFilterParams({});
    setFilterResetKey((prev) => prev + 1);
  };

  return (
    <>
      <Dialog
        open={showDialog}
        onClose={closeDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth={true}
        maxWidth={"xl"}
      >
        <DialogTitle id="responsive-dialog-title">
          <Typography variant="h5" color="black">
            Assign - Order# {orderNumber}
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closeDialog}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: "black",
          })}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent dividers>
          <DialogContentText id="alert-dialog-description">
            <Box>
              <Box
                sx={{
                  display: "flex",
                  border: "1px solid #C4C4C4",
                  p: 1,
                  borderTopLeftRadius: 4,
                  borderTopRightRadius: 4,
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    color: "rgba(0, 0, 0, 0.87)",
                    fontWeight: "520",
                  }}
                >
                  Scheduled
                </Typography>
                <Typography
                  sx={{
                    width: 200,
                    color: "rgba(0, 0, 0, 0.87)",
                    fontWeight: "520",
                  }}
                >
                  Inspector Due
                </Typography>
                <Typography
                  sx={{
                    flex: 1,
                    color: "rgba(0, 0, 0, 0.87)",
                    fontWeight: "520",
                  }}
                >
                  Client Due
                </Typography>
                <Typography
                  sx={{
                    flex: 1,
                    color: "rgba(0, 0, 0, 0.87)",
                    fontWeight: "520",
                  }}
                >
                  ETA
                </Typography>
                <Typography
                  sx={{
                    flex: 1,
                    color: "rgba(0, 0, 0, 0.87)",
                    fontWeight: "520",
                  }}
                >
                  Rush
                </Typography>
                <Typography
                  sx={{
                    flex: 1,
                    color: "rgba(0, 0, 0, 0.87)",
                    fontWeight: "520",
                  }}
                >
                  State
                </Typography>
                <Typography
                  sx={{
                    flex: 1,
                    color: "rgba(0, 0, 0, 0.87)",
                    fontWeight: "520",
                  }}
                >
                  Zip
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  borderBottom: "1px solid #C4C4C4",
                  borderLeft: "1px solid #C4C4C4",
                  borderRight: "1px solid #C4C4C4",
                  p: 1,
                  borderBottomLeftRadius: 4,
                  borderBottomRightRadius: 4,
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                  }}
                >
                  {orderDetails?.appointmentDate
                    ? dayjs(orderDetails.appointmentDate).format("MM/DD/YYYY")
                    : "-"}
                </Typography>
                <Box sx={{ width: 200 }}>
                  <Typography>
                    {" "}
                    {orderDetails?.inspectorDueDate
                      ? dayjs(orderDetails.inspectorDueDate).format(
                          "MM/DD/YYYY"
                        )
                      : "-"}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    flex: 1,
                  }}
                >
                  {orderDetails?.clientDueDate
                    ? dayjs(orderDetails.clientDueDate).format("MM/DD/YYYY")
                    : "-"}
                </Typography>
                <Typography
                  sx={{
                    flex: 1,
                  }}
                >
                  {getEta()}
                </Typography>
                <Typography
                  sx={{
                    flex: 1,
                  }}
                >
                  {orderDetails?.rushFeeLabel ? "Yes" : "No"}
                </Typography>
                <Typography
                  sx={{
                    flex: 1,
                  }}
                >
                  {orderDetails?.propertyRecord?.state || "-"}
                </Typography>
                <Typography
                  sx={{
                    flex: 1,
                  }}
                >
                  {orderDetails?.propertyRecord?.zip || "-"}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 3,
                  mb: 1.5,
                  ml: 1,
                }}
              >
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={outsideCoverage}
                        onChange={(e) => setOutsideCoverage(e.target.checked)}
                      />
                    }
                    label="Show Unqualified and/or Outside Coverage Area"
                  />
                </FormGroup>
                <Box>
                  <IconButton
                    aria-label="clear-all"
                    onClick={onClearAllFilters}
                  >
                    <ClearAllOutlined color="primary" />
                  </IconButton>
                  <IconButton
                    aria-label="filter"
                    onClick={() => onFilterClick(!showTableFilter)}
                  >
                    {showTableFilter ? (
                      <FilterAltOffOutlinedIcon color="primary" />
                    ) : (
                      <FilterAltOutlinedIcon color="primary" />
                    )}
                  </IconButton>
                </Box>
              </Box>

              <DataGrid
                key={"manual-assign-table-" + filterResetKey}
                sx={{
                  ".MuiDataGrid-columnSeparator": {
                    display: "none",
                  },
                  ".MuiDataGrid-columnHeaderTitle": {
                    fontWeight: "bold !important",
                  },
                  ".MuiDataGrid-columnHeaderTitleContainer": {
                    alignItems: "flex-start",
                    marginTop: "15px",
                  },
                  ".MuiDataGrid-columnHeaderTitleContainerContent": {
                    pt: 1,
                  },
                  "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-checkboxInput":
                    {
                      display: "none",
                    },
                }}
                rows={rowData}
                columns={columns}
                disableColumnMenu
                disableColumnResize
                checkboxSelection
                disableMultipleRowSelection
                hideFooter
                onRowSelectionModelChange={(newRowSelectionModel: any) => {
                  setRowSelectionModel(newRowSelectionModel);
                }}
                rowSelectionModel={rowSelectionModel}
                columnHeaderHeight={headerHeight}
              />

              {rowSelectionModel.length > 0 && (
                <Box className="others-row-layout" sx={{ mt: 2 }}>
                  <Typography className="label-txt">
                    Note to Inspection Firm <br />
                    (Optional)
                  </Typography>
                  <TextField
                    fullWidth
                    id="inspector-note"
                    variant="outlined"
                    value={inspectionNote}
                    onChange={(e) => setInspectionNote(e.target.value)}
                  />
                </Box>
              )}
            </Box>
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Button
              sx={{ ml: 1 }}
              variant="outlined"
              onClick={onRecruitingClick}
            >
              Move to Recruiting
            </Button>
            <Box>
              <Button variant="outlined" onClick={closeDialog} type="reset">
                Cancel
              </Button>
              <Button
                sx={{ ml: 2 }}
                variant="contained"
                onClick={onAssignClick}
                disabled={rowSelectionModel.length === 0}
              >
                Assign
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title={errorMessage}
        />
      )}
      {showLoading && <Loading />}
    </>
  );
}
